// MF-67 — Set the school's geo centre. Search the school address (free OSM geocoding) to jump the map
// near the school, then drop/adjust the pin and save. The Journal location picker centres on this.
// No Google/API key involved.
import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import { Typography, TextField, Button } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import MapPicker from '../../../Journal/components/MapPicker'
import { updateGeo } from '../../../../../services/tenant/tenant.service'
import { geocodeAddress } from '../../../../../services/geocode.service'
import { updateTenantGeo } from '../../../../../store/actions/tenant.actions'

const SchoolLocation = ({ permission, tenant = {} }) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [geo, setGeo] = useState({ lat: null, lng: null })
  const [recenterTo, setRecenterTo] = useState(null)
  const [saving, setSaving] = useState(false)
  const [searching, setSearching] = useState(false)
  const [address, setAddress] = useState('')

  // Default the search box to the school's address on file.
  const defaultAddress = useMemo(
    () => [tenant?.address, tenant?.state, tenant?.country].filter(Boolean).join(', '),
    [tenant?.address, tenant?.state, tenant?.country]
  )

  useEffect(() => {
    setAddress(defaultAddress)
  }, [defaultAddress])

  // Prefill + centre the map on the saved school location once the tenant loads (the fetch is async,
  // so the map mounts before geo arrives; MapContainer's center is initial-only, hence the recenter).
  useEffect(() => {
    if (tenant?.geo?.lat != null && tenant?.geo?.lng != null) {
      setGeo({ lat: tenant.geo.lat, lng: tenant.geo.lng })
      setRecenterTo({ lat: tenant.geo.lat, lng: tenant.geo.lng })
    }
  }, [tenant?.geo?.lat, tenant?.geo?.lng])

  const onSearch = async () => {
    if (!address.trim()) return
    setSearching(true)
    try {
      const hit = await geocodeAddress(address)
      if (hit) {
        setRecenterTo({ lat: hit.lat, lng: hit.lng })
        // Drop a suggested pin at the match; the user fine-tunes it by dragging/clicking.
        setGeo({ lat: hit.lat, lng: hit.lng })
      } else {
        enqueueSnackbar('Address not found — try a simpler search or place the pin manually', {
          variant: 'warning'
        })
      }
    } finally {
      setSearching(false)
    }
  }

  const canSave = geo.lat != null && geo.lng != null && !saving && permission

  const onSave = async () => {
    setSaving(true)
    try {
      await updateGeo({ geo })
      dispatch(updateTenantGeo(geo))
      enqueueSnackbar('School location saved', { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error?.description || 'Could not save location', {
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Box className='bg-primary text-primary-foreground px-4 py-1 flex gap-4 items-center justify-between h-8'>
        <Typography variant='subtitle1' className='uppercase'>
          School location
        </Typography>
        {permission ? (
          <ButtonStartIcon
            color='secondary'
            variant='contained'
            startIcon={['fal', 'check']}
            disabled={!canSave}
            loading={saving}
            onClick={onSave}
          >
            Save
          </ButtonStartIcon>
        ) : null}
      </Box>
      <Box className='px-2 pt-4 pb-4 flex flex-col gap-3'>
        <div className='flex gap-2 items-start'>
          <TextField
            className='grow'
            size='small'
            label='Search the school address'
            placeholder='e.g. 90 Harold Street, Wantirna VIC 3152'
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          />
          <Button variant='outlined' onClick={onSearch} disabled={searching || !address.trim()}>
            {searching ? 'Searching…' : 'Find'}
          </Button>
        </div>
        <Typography variant='caption' className='opacity-50'>
          Find the school by address to jump the map, then click/drag the pin to the exact spot.
        </Typography>
        <MapPicker
          value={geo}
          center={geo}
          recenterTo={recenterTo}
          onChange={setGeo}
          height={320}
          zoom={16}
        />
        {geo.lat != null ? (
          <Typography variant='caption' className='opacity-50'>
            {`Centre: ${Number(geo.lat).toFixed(5)}, ${Number(geo.lng).toFixed(5)}`}
          </Typography>
        ) : null}
      </Box>
    </>
  )
}

export default SchoolLocation
